// src/lib/carte-dite.js — CE QUE LA CARTE DIT À VOIX HAUTE.
//
// Dimitri, 2026-09-22 : « que je puisse valider à la voix une carte que je
// n'ai pas entendue, c'est un risque réel. »
//
// ══ CE QUE CE MODULE EST, ET CE QU'IL N'EST PAS ════════════════════════════
//
// Une fonction PURE. Aucune lecture, aucune horloge, aucun React. Elle reçoit
// des valeurs DÉJÀ DÉRIVÉES PAR LA CARTE — le nom du logement, le libellé du
// champ, la valeur actuelle, la valeur suivante — et elle rend deux phrases.
//
// ⚠️ ELLE NE RECALCULE RIEN. La carte dérive déjà tout cela pour l'afficher
// (`CarteGesteDemande.jsx`, l'aperçu). Recalculer ici ferait diverger CE QUI SE
// LIT de CE QUI S'AFFICHE — et la divergence serait muette : les deux
// continueraient de dire quelque chose de plausible.
//
// ══ DEUX ÉNONCÉS, ET UN SEUL POSE « LU » ═══════════════════════════════════
//
// Le défaut que la revue métier a trouvé : une carte de prix fait ~190
// caractères, soit treize secondes à la vitesse où il lit. Un « oui » à la
// neuvième seconde tombait dans la branche « coupure », le drapeau retombait,
// et le « oui » — un seul mot — était jeté par le plancher SANS UN MOT. La
// carte s'arrêtait net et rien ne répondait.
//
//   ÉNONCÉ 1 — LE FAIT, court (110 caractères, ~8 s). C'est LUI, et lui seul,
//              qui pose « il l'a entendue ».
//   ÉNONCÉ 2 — LE RESTE (140) : les avertissements, puis l'invitation ou le
//              motif du blocage. Il n'ajoute rien au verdict.
//
// Un « oui » pendant l'énoncé 1 est refusé à voix haute, puis le fait est
// relu. Pendant l'énoncé 2, le fait est entendu : on traite normalement.
//
// ══ L'OPÉRATEUR NE PRONONCE JAMAIS UNE FORME DE « LANCE » ══════════════════
//
// ⚠️ GARDE STRUCTURELLE, et elle vaut mieux qu'un seuil de similarité à 60 %.
// Si l'invitation disait « dites oui », le mot déclencheur sortirait du
// haut-parleur — et pourrait y revenir par le micro. Aucune forme fermée de
// `LANCE` (`src/lib/operateur-acquiescement.js`) ne sort d'ici.
//
// ══ CE QUI N'EST JAMAIS LU ═════════════════════════════════════════════════
//
// · `constatable` — jusqu'à 815 caractères, et c'est ce qu'on ira VÉRIFIER
//   après : sa place est à l'écran, pas dans l'oreille.
// · le CORPS d'un message au voyageur (`args_textes`) — texte libre destiné à
//   un tiers, jusqu'à quatre mille caractères. La voix ne permet pas de
//   revenir en arrière ; il se relit à l'écran.
// · un identifiant de logement (`lc1`, `l8`). Quand le nom manque, la voix DIT
//   qu'elle ne sait pas nommer — elle ne récite pas le code.

import { montantDit, dateDite, nombreEnLettres, coupeLisible } from "./voix";
// ⚠️ LE REGISTRE FAIT FOI, PAS LE GESTE SERVI (revue adverse #1482, CRITIQUE).
// Voir `refuseALaVoix`.
import { lireLevier } from "./operateur-leviers";

// Les primitives de lecture vivent dans l'entonnoir (`voix.js`) pour qu'il n'y
// en ait qu'un seul jeu, partagé par la prose et par la carte. On les re-expose
// ici : c'est de ce module qu'on les attend.
export { montantDit, dateDite, nombreEnLettres };

// ══ LA GARDE ARGENT — SÉPARÉE DE LA VANNE ══════════════════════════════════
//
// ⚠️ IL N'EXISTE AUCUNE RÉTRACTATION APRÈS UN LANCEMENT VOCAL. Le « non » ne
// retire qu'une carte NON LANCÉE ; une fois le geste parti, rien ne le
// rappelle. Un geste qui porte un montant, qui supprime, qui sort de chez FBZ
// — ou dont le registre dit qu'il ne revient pas — ne part donc JAMAIS d'un
// « oui » entendu, que la vanne du « oui » vocal soit ouverte ou fermée. C'est
// une condition à part, et elle se teste à part, VANNE FERMÉE COMPRISE.
export const MARQUAGES_JAMAIS_A_LA_VOIX = ["argent", "supprime", "sortant"];

// ══ RIEN QUI ATTEIGNE UNE VRAIE PERSONNE ═══════════════════════════════════
//
// Règle Dimitri, 2026-09-22 : « rien qui atteigne une vraie personne ne se
// lance sans que je l'aie décidé. »
//
// ⚠️ `sortant` NE SUFFISAIT PAS. Quatre leviers déclarent `ou_ca_part:["agent"]`
// sans porter ce marquage : `menage_run_statut_set`, `checklist_point_ajouter`,
// `stock_signaler`, `menage_instruction_archive`. Ils ne
// « sortent » pas de chez FBZ — ils atteignent quand même quelqu'un, à qui on
// demande de faire ou de ne plus faire quelque chose. Un « oui » mal entendu en
// conduisant changerait la journée d'un agent, et personne ne le saurait avant
// qu'il s'en étonne.
//
// La seule destination inoffensive est `personne` (le registre l'écrit ainsi) ;
// `agent`, `voyageur`, `proprietaire`, `beds24`, `boite_mail`, `page_publique`,
// `atteignable` désignent tous quelqu'un ou quelque chose au-dehors.
//
// ⚠️ FAIL-CLOSED DANS LE BON SENS : `ou_ca_part` ABSENT vaut « rien » (le
// registre ne le déclare pas sur les leviers purement internes) ; une valeur
// INCONNUE, elle, refuse — un jour on en ajoutera une, et elle ne doit pas
// gagner un droit en étant neuve.
const DESTINATIONS_SANS_PERSONNE = ["personne"];

// ⚠️ NOMMÉS, FAUTE DE MARQUAGE QUI LE DISE — ET CHACUN AVEC SON MOTIF.
// `divulgation_regle_clear` ÉLARGIT ce que le copilote a le droit de dire à un
// voyageur : la table de divulgation ne peut que RESTREINDRE (elle est un
// plafond), et retirer une règle rouvre la parole. Aucun marquage du registre
// ne porte ce sens — il n'en existe que trois, tous sur l'argent, la
// suppression et la sortie.
// `orchestrateur_incident_rachat_depot` (lot 3 des repères, majeur 4) date une
// dégradation retrouvée PAR RESSEMBLANCE (« la lampe », dans un incident où la
// précision vaut souvent "") — deux dates reposables, donc pas de
// `sans_retour`, mais une dégradation choisie à l'oreille n'est pas une
// dégradation choisie. Le motif était codé en dur (« il élargit ce que le
// copilote peut dire ») : sur une date de rachat, c'eût été un mensonge
// plausible. D'où la table.
const LEVIERS_JAMAIS_A_LA_VOIX = Object.freeze({
  divulgation_regle_clear: "élargit ce que le copilote peut dire à un voyageur",
  orchestrateur_incident_rachat_depot: "vise une dégradation par ressemblance",
  // ⚠️ CEINTURE ET BRETELLES (lot 6, revue adverse). Les dix leviers d'argent
  // sont déjà refusés à la voix par leurs MARQUAGES — mesuré 10/10 au harnais.
  // Mais `compta_ligne_ajouter` est le SEUL à n'avoir qu'UNE barrière :
  // `marquages:["argent"]`, `sans_retour:null`, `ou_ca_part:["personne"]`.
  // Retirer « argent » un jour, en se disant qu'une ligne de compta n'est pas
  // de la facturation, rouvrirait à la voix un geste qui écrit dans le
  // résultat net — sans que rien ne le signale. Le nommer ici fait de ce
  // refus une décision, pas une conséquence.
  compta_ligne_ajouter: "écrit un montant dans votre résultat net",
});

// ══ CE QUE LA VOIX DIT AUTREMENT QUE L'ÉCRAN ═══════════════════════════════
//
// ⚠️ « NON » EST UNE FORME FERMÉE DE RÉTRACTATION (`ANNULE`,
// operateur-acquiescement.js). L'écran Incidents dit « Non récupérable » — c'est
// son vocabulaire canonique, et la carte l'affiche tel quel. Mais prononcé,
// « non » sortirait du haut-parleur et pourrait revenir par le micro : la
// carte que Dimitri vient de préparer se retirerait sur son propre écho. La
// voix dit donc « irrécupérable » — même sens, aucun token.
const DITS_A_LA_VOIX = Object.freeze([
  [/\bnon récupérable\b/gi, "irrécupérable"],
  [/\bnon jugée?s?\b/gi, "pas encore jugée"],
]);
function _ditALaVoix(t) {
  let out = String(t);
  for (const [re, par] of DITS_A_LA_VOIX) out = out.replace(re, par);
  return out;
}

/** Ce qu'un marquage DIT, sans emoji et sans jargon. */
const DITS = {
  argent: "porte un montant",
  supprime: "supprime, et ce qui part ne revient pas tout seul",
  sortant: "sort de chez vous",
};

export const MAX_FAIT = 110;
export const MAX_RESTE = 140;

/** Ce que la voix dit quand l'écran, lui, retomberait sur « lc1 ». */
export const SANS_NOM = "sur un logement que je ne sais pas nommer";

// ══ LA NUIT VENDUE : ENTENDUE AVANT, RAPPELÉE PENDANT ══════════════════════
//
// Dimitri, 2026-09-22 : « si je valide sans savoir que la nuit est prise,
// c'est raté. »
//
// ══ ET IL N'Y A PAS D'ÉNONCÉ 0 — C'EST UNE MESURE, PAS UN CHOIX DE STYLE ═══
//
// ⚠️ LA PHRASE EST DÉJÀ DITE, ENTIÈREMENT, AVANT L'ÉNONCÉ 1. Le serveur la
// pose EN TÊTE de `reponse.texte` (`lib/operateur-nuit-vendue.js::_poser`) —
// et `reponse.texte` EST la prose que la conversation prononce, en une seule
// séquence d'état, avant d'enchaîner sur la carte (`puisLaCarte` n'est que le
// `onFini` de la prose). Un énoncé 0 la dirait donc une SECONDE fois, et
// « jamais deux fois le même texte » est une règle de Dimitri (15/09).
//
// ⚠️ ET LA COUPE DES SEPT CENTS CARACTÈRES NE L'EMPORTE PAS : `coupeLisible`
// garde la TÊTE et jette la queue. Une phrase placée en tête survit à toutes
// les proses, y compris les plus longues. C'est ce qui rend l'énoncé 0
// inutile — pas une préférence, une propriété du code, et elle est verrouillée
// par un test (`conversation-vocale.carte.test.js`).
//
// ⚠️ CE QUI RESTE À FAIRE, ALORS : L'ORDRE EST BON, MAIS L'OREILLE OUBLIE. Il
// y a une prose entière entre l'avertissement et le chiffre. On rappelle donc
// le GENRE en tête de l'énoncé 1 — vingt caractères, pas la phrase — pour que
// l'alerte tienne jusqu'au moment de CLIQUER.
//
// ⚠️ « JUSQU'AU CLIC », ET PAS « JUSQU'AU OUI » — C'EST MESURÉ (revue #1488,
// MAJEUR 2). Le seul levier qui produise un genre est `pricing_set_manual`
// (`LEVIERS_CONCERNES`), et il porte `argent` : `refuseALaVoix` le REFUSE, et
// la garde argent passe avant la vanne comme avant la garde d'empreinte dans
// `envoyerQuestion`. AUCUNE carte marquée n'est donc lançable à la voix
// aujourd'hui — l'énoncé 2 dit « cliquez-la », et le marqueur est la dernière
// chose entendue avant ce clic. Le chemin « oui + marqueur » existe dans le
// code mais n'est pas exercé en prod : le jour où un levier non monétaire
// entrera dans `LEVIERS_CONCERNES`, il faudra l'éprouver pour de bon.
export const MARQUEURS = Object.freeze({
  vendue: "Nuit déjà vendue —",
  passee: "Nuit passée —",
  bloquee: "Dates fermées —",
  inconnue: "Non vérifié —",
});

/**
 * LE MARQUEUR, POSÉ EN TÊTE DE L'ÉNONCÉ 1.
 *
 * ⚠️ LE FAIT CHIFFRÉ NE SAUTE JAMAIS — c'est la règle de tout ce module, et
 * elle vaut plus que le plafond. Le marqueur ne se paie donc PAS sur le fait :
 * s'il ne tient pas dans `MAX_FAIT`, c'est le TITRE qui cède (il ne revient de
 * toute façon que quand la prose s'est tue), et si même sans titre ça déborde,
 * le plafond s'élève de la longueur du marqueur. Borner ici couperait la
 * QUEUE de l'énoncé — c'est-à-dire le montant, c'est-à-dire tout.
 *
 * ⚠️ IDEMPOTENT, ET SUR N'IMPORTE QUEL MARQUEUR. La relecture d'un « oui »
 * trop tôt repasse par le même chemin. Ne couper que sur le MÊME marqueur
 * laissait composer « Nuit passée — Nuit déjà vendue — … » : on refuse dès
 * qu'un marqueur, quel qu'il soit, ouvre déjà l'énoncé.
 *
 * ⚠️ RIEN À MARQUER QUAND RIEN NE SE DÉCIDE (`pose: false`). Ces énoncés-là
 * disent « je ne peux pas vous la dire en entier, elle est à l'écran » : une
 * alerte d'argent en tête d'une phrase qui ne nomme pas son objet
 * (« Nuit déjà vendue — un geste est prêt, sur un logement que je ne sais pas
 * nommer ») avertit de quelque chose qu'elle ne décrit pas.
 *
 * @param {object} p — ce que `phraseDeLaCarte` a rendu
 * @param {string|null} type — le genre posé par le serveur (`avant_carte_type`)
 * @param {string} [titre] — pour savoir ce qui peut céder, jamais pour le dire
 */
export function avecMarqueur(p, type, titre) {
  if (!p || typeof p !== "object") return p;
  const m = MARQUEURS[type];
  if (!m || !p.fait || p.pose === false) return p;
  const ti = String(titre || "").trim();
  const dejaMarque = (t) =>
    Object.values(MARQUEURS).some((x) => t.startsWith(x));
  const poser = (brut) => {
    let t = String(brut || "").trim();
    if (!t) return t;
    if (dejaMarque(t)) return t;
    const avec = `${m} ${t}`;
    if (avec.length > MAX_FAIT && ti && t.startsWith(`${ti}.`)) {
      const sansTitre = t.slice(ti.length + 1).trim();
      if (sansTitre) t = sansTitre;
    }
    return `${m} ${t}`;
  };
  return {
    ...p,
    fait: poser(p.fait),
    faitNu: poser(p.faitNu || p.fait),
  };
}

/** Les marquages du registre, jamais ceux du geste servi. Voir `refuseALaVoix`. */
function _marquagesAuRegistre(nom) {
  const l = lireLevier(nom);
  return l && Array.isArray(l.marquages) ? l.marquages : [];
}

// ⚠️ IL N'Y A PLUS DE `jamaisALaVoix(marquages)`, ET C'EST VOLONTAIRE. Prendre
// des marquages en argument invitait à passer ceux du GESTE — ce qui rendait la
// garde muette sur toute carte d'événement (elle n'en porte pas) et sur les
// gestes dont `_marquagesDuGeste` avait retiré `argent`. Une signature qui
// accepte la mauvaise source finit par la recevoir : on ne prend plus qu'un
// levier, et on va le lire au registre.

/**
 * CE GESTE PEUT-IL PARTIR D'UN « OUI » ENTENDU ?
 *
 * ⚠️ TROIS DÉFAUTS QUE CETTE FONCTION FERME, ET AUCUN NE SE VOYAIT.
 *
 * 1. LE GESTE SERVI NE PORTE PAS TOUJOURS SES MARQUAGES. Les cartes
 *    d'ÉVÉNEMENT (`lib/operateur-evenements.js`) se composent en
 *    `{levier, args}` et rien d'autre : `marquages` n'est posé que par
 *    `lib/operateur-geste.js` (`_marquagesDuGeste`), qui n'est pas sur ce
 *    chemin. Lire `geste.marquages` rendait donc la garde MUETTE sur tout le
 *    fil d'événements — dont `pricing_apply_base` (argent + sortant), qui
 *    POUSSE UN PRIX CHEZ BEDS24, et `taxe_reminder_send` (sortant).
 *
 * 2. ET LES MARQUAGES DU GESTE SONT D'AFFICHAGE, PAS D'AUTORISATION.
 *    `_marquagesDuGeste` RETIRE `argent` quand l'argument de montant est
 *    absent — c'est juste pour la carte (« ce geste porte un montant » serait
 *    faux). Mais `pricing_set_min_price` SANS `min_price` efface le plancher
 *    bloquant (CLAUDE.md : `min_price null` = jamais de blocage) : un geste
 *    d'argent sans le mot « argent ». On lit donc le REGISTRE, toujours.
 *
 * 3. `sans_retour` DIT EXACTEMENT CE QUE LE MODULE PROMET. Dix-huit leviers le
 *    déclarent sans porter aucun marquage — « le stock central est débité »,
 *    « l'agent d'avant en est retiré », « une chaîne vide EFFACE le champ ».
 *    Le sens de la garde est « rien d'irrattrapable à la voix » : `sans_retour`
 *    EST cette phrase, écrite par le registre. Il entre dans la garde.
 *
 * ⚠️ FAIL-CLOSED : un levier que le registre ne connaît pas ne part jamais à
 * la voix. Un geste forgé, ou servi par une version antérieure, ne doit pas
 * gagner un droit en étant inconnu.
 *
 * ⚠️ LE MOTIF EST UN COMPLÉMENT SANS SUJET — « porte un montant », « ne se
 * rattrape pas », « vise une dégradation par ressemblance » — parce qu'il se
 * lit derrière « Ce geste … » dans l'énoncé 2 (revue lot 3, majeur 2 : « Ce
 * geste ça ne se rattrape pas » n'était pas du français, et « Ce geste il
 * vise… » non plus). Un motif neuf s'écrit dans cette forme, ou le gabarit
 * le rend illisible à la voix.
 *
 * @returns {{refuse:boolean, motif:string|null}}
 */
export function refuseALaVoix(geste) {
  const nom = geste && typeof geste === "object" ? geste.levier : geste;
  const l = lireLevier(nom);
  if (!l)
    return {
      refuse: true,
      // Un complément, comme les autres : « Ce geste m'est inconnu ».
      motif: "m'est inconnu, je ne sais pas ce qu'il fait",
    };
  // ⚠️ LE NOM PASSE AVANT LE MARQUAGE (lot 6), ET C'EST UNE CORRECTION. Un
  // levier nommé ici l'est pour une raison PRÉCISE, que son marquage ne dit
  // pas : « il porte un montant » est vrai de `compta_ligne_ajouter`, mais ce
  // n'est pas pour ça qu'on refuse de le prononcer — c'est parce qu'il écrit
  // dans le résultat net et qu'il n'a qu'une seule barrière. La table gardait
  // son motif pour elle tant qu'un marquage tombait d'abord.
  if (Object.prototype.hasOwnProperty.call(LEVIERS_JAMAIS_A_LA_VOIX, nom))
    return { refuse: true, motif: LEVIERS_JAMAIS_A_LA_VOIX[nom] };
  const marques = _marquagesAuRegistre(nom).filter((m) =>
    MARQUAGES_JAMAIS_A_LA_VOIX.includes(m),
  );
  if (marques.length)
    return {
      refuse: true,
      motif: marques.map((m) => DITS[m]).join(", il "),
    };
  if (l.sans_retour != null)
    return { refuse: true, motif: "ne se rattrape pas" };
  // ⚠️ `ou_ca_part` est glosé par le serveur en `{texte}` sur le GESTE, mais le
  // registre, lui, porte des CLÉS. On lit le registre — comme partout ici.
  const ou = Array.isArray(l.ou_ca_part) ? l.ou_ca_part : [];
  const dehors = ou.filter((d) => !DESTINATIONS_SANS_PERSONNE.includes(d));
  if (dehors.length) return { refuse: true, motif: "atteint quelqu'un" };
  return { refuse: false, motif: null };
}

/**
 * Borne une phrase, en la coupant à la phrase — jamais au milieu d'un mot.
 * Sans suffixe : ce n'est pas une prose qu'on abrège, c'est un énoncé qu'on
 * tient court par construction.
 */
export function borner(texte, max) {
  const t = String(texte || "")
    .replace(/\s+/g, " ")
    .trim();
  if (t.length <= max) return t;
  const tete = t.slice(0, max);
  const phrase = /[.!?…]\s/g;
  let coupe = -1;
  let m;
  while ((m = phrase.exec(tete)) !== null) coupe = m.index + 1;
  if (coupe <= 0) coupe = tete.lastIndexOf(" ");
  if (coupe <= 0) coupe = max;
  return t.slice(0, coupe).trim();
}

/**
 * Une valeur de l'aperçu, dite.
 *
 * ⚠️ AUCUNE ISO NE TRAVERSE. Le serveur compose l'essentiel de ses libellés,
 * mais une date brute passe encore par endroits (« 2026-10-12 »), et la
 * synthèse en fait « deux mille vingt-six tiret dix tiret douze ». Les
 * montants, eux, sont écrits par l'entonnoir de `voix.js` au moment de dire —
 * ici on ne les touche pas deux fois.
 */
export function direValeur(v) {
  return _ditALaVoix(_direValeurBrute(v));
}
function _direValeurBrute(v) {
  const t = String(v === null || v === undefined ? "" : v).trim();
  if (!t) return t;
  const plage =
    /^(\d{4}-\d{2}-\d{2})\s*(?:→|->|au|-)\s*(\d{4}-\d{2}-\d{2})$/.exec(t);
  if (plage) return dateDite(plage[1], plage[2]) || t;
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return dateDite(t) || t;
  // ⚠️ ET LE FORMAT FRANÇAIS, QUI TRAVERSAIT. « 02/01 → 08/01 » se disait
  // « 02/01 devient 08/01 » : la règle générique de la flèche l'emportait, et
  // on entendait deux nombres reliés par un verbe — aucune date.
  //
  // ⚠️ L'ANNÉE NE S'INVENTE PAS. Sans elle on dit « du 2 au 8 janvier » et
  // rien de plus : annoncer une mauvaise année sur un blocage de calendrier
  // serait pire que de n'en annoncer aucune.
  const fr = (v) => {
    const m = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/.exec(v);
    if (!m) return null;
    const an = m[3] ? (String(m[3]).length === 2 ? `20${m[3]}` : m[3]) : null;
    const j = m[1].padStart(2, "0");
    const mo = m[2].padStart(2, "0");
    return { jour: j, mois: mo, an };
  };
  const pf =
    /^(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)\s*(?:→|->|au|-)\s*(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)$/.exec(
      t,
    );
  if (pf) {
    const a = fr(pf[1]);
    const b = fr(pf[2]);
    if (a && b) {
      // Sans année écrite, on compose sur l'année en cours : `dateDite` ne la
      // dira donc pas, et rien de faux ne sort.
      const courante = String(new Date().getFullYear());
      const dit = dateDite(
        `${a.an || b.an || courante}-${a.mois}-${a.jour}`,
        `${b.an || a.an || courante}-${b.mois}-${b.jour}`,
      );
      if (dit) return dit;
    }
  }
  const seule = fr(t);
  if (seule && seule.an)
    return dateDite(`${seule.an}-${seule.mois}-${seule.jour}`) || t;
  return t;
}

// ══ UN FAIT, EN UNE PHRASE — CE QUI SE LIT N'EST PAS CE QUI S'AFFICHE ══════
//
// ⚠️ MESURÉ EN PROD LE 23/09, au casque :
//
//   « Nuit déjà vendue — Sur un Nuage: prix par nuit trente-quatre euros
//     (1 nuit) · 1 nuit déjà vendue · plancher trente euros devient
//     soixante-dix-neuf euros. »
//
// Trois choses ne se disent pas. Le « · » : à l'œil c'est une colonne, à
// l'oreille c'est un silence — on entend trois faits collés sans savoir où
// l'un finit. La parenthèse : « trente-quatre euros parenthèse une nuit »
// n'existe pas à voix haute. Et la REDITE : « 1 nuit déjà vendue » répète mot
// pour mot le marqueur que ce même énoncé vient de poser en tête — « jamais
// deux fois le même texte » (règle Dimitri, 15/09).
//
// L'aperçu a raison de tout afficher : Dimitri lit la carte et compare. La
// VOIX, elle, dit LE fait — le reste est à l'écran, à trente centimètres.

/**
 * La valeur d'aperçu réduite à son premier fait.
 *
 * `« 34 € (1 nuit) · 1 nuit déjà vendue · plancher 30 € »` → `« 34 € »`
 *
 * ⚠️ LE PREMIER SEGMENT, PAS LE PLUS COURT NI LE PLUS RICHE. Les lectures
 * composent leurs morceaux dans l'ordre de la décision (`morceauxPrix`,
 * lib/pricing-lectures-apercu.js) : le fait qui décide vient d'abord, les
 * repères ensuite. Prendre autre chose serait un choix ; prendre le premier
 * suit ce que le serveur a déjà ordonné.
 */
export function unSeulFait(v) {
  const t = String(v === null || v === undefined ? "" : v);
  const premier = t.split("·")[0];
  return premier
    .replace(/\s*\([^()]*\)/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/** Une valeur d'aperçu présentable ? « — » et le vide n'en sont pas. */
function presentable(v) {
  if (v === null || v === undefined) return false;
  const t = String(v).trim();
  return !!t && t !== "—" && t !== "-";
}

/**
 * La valeur d'aujourd'hui est-elle une valeur CHIFFRÉE ?
 *
 * ⚠️ LA TÊTE, PAS « CONTIENT UN CHIFFRE ». « aucun prix sur 1 nuit » porte un
 * chiffre et n'est pas une valeur ; « 34 € » en est une. C'est ce qui OUVRE la
 * chaîne qui décide — et c'est ce qui rend « passe de … à … » lisible ou non.
 */
function _estChiffree(v) {
  return /^-?\d/.test(String(v).trim());
}

/**
 * LES DEUX ÉNONCÉS D'UNE CARTE.
 *
 * @param {object} e — les valeurs DÉJÀ dérivées par la carte
 *   - titre         {string}  du registre, jamais du modèle
 *   - marquages     {string[]}
 *   - nomLogement   {string|null}  le NOM ; jamais l'identifiant
 *   - aUnLogement   {boolean} la lecture porte-t-elle sur un logement ?
 *   - libelle       {string}  le champ, en français
 *   - quand         {string|null} la période lue (pricing) — le sujet de la
 *                   phrase quand elle existe ; le libellé se tait alors
 *   - actuelLisible {string}  la valeur d'aujourd'hui
 *   - aUneSuite     {boolean} y a-t-il un « après » à annoncer ?
 *   - nouvelleLisible {string}
 *   - unite         {string}
 *   - bloque        {boolean} le bouton est gris
 *   - raisonBlocage {string}  pourquoi, en une phrase (de la carte)
 *   - prose         {string}  ce qu'il vient de dire — pour ne pas se répéter
 *   - vanneOuverte  {boolean} le « oui » vocal est-il armé ?
 *
 * @returns {{fait:string, reste:string, pose:boolean}|null}
 *   `pose` — cette lecture peut-elle poser « il l'a entendue » ? Non quand on
 *   n'a pas su nommer le logement d'un geste d'argent : on le DIT (jamais de
 *   silence), mais un « oui » ne s'appuiera pas dessus.
 */
export function phraseDeLaCarte(e) {
  if (!e || typeof e !== "object") return null;
  const titre = String(e.titre || "").trim();
  // ⚠️ AU REGISTRE, comme la garde de l'énoncé 2 : un geste d'événement ne
  // porte pas ses marquages, et le geste servi peut les avoir perdus.
  const sensible = refuseALaVoix(
    e.levier !== undefined ? e.levier : { levier: null },
  ).refuse;
  const nom = presentable(e.nomLogement) ? String(e.nomLogement).trim() : null;
  // ⚠️ L'ÉCRAN, LUI, RETOMBE SUR L'IDENTIFIANT quand le nom manque
  // (`apercu.data.logement?.nom || apercu.data.logement?.id`). La voix ne
  // récite pas un code : elle dit qu'elle ne sait pas nommer.
  const innommable = !nom && e.aUnLogement === true;

  // ── UNE CARTE D'ARGENT SANS LOGEMENT NOMMÉ NE SE LIT PAS ─────────────────
  // Un prix appartient à un logement. L'annoncer sans savoir lequel serait la
  // seule chose pire qu'un silence : une phrase juste sur un objet inconnu.
  if (sensible && innommable) {
    return {
      fait: `Un geste est prêt, ${SANS_NOM} — il est à l'écran.`,
      faitNu: `Un geste est prêt, ${SANS_NOM} — il est à l'écran.`,
      reste: "Je ne fais pas ça à la voix : regardez la carte.",
      pose: false,
    };
  }

  // ── ÉNONCÉ 1 — LE FAIT ───────────────────────────────────────────────────
  const aUneSuite = e.aUneSuite === true && presentable(e.nouvelleLisible);
  // ══ QUAND LA LECTURE DIT SUR QUOI PORTE LE GESTE — « le 27 septembre » ═══
  //
  // ⚠️ SEULES LES LECTURES DE PÉRIODE LE RENDENT (`corps.periode`, pricing).
  // Là où il existe, c'est LUI le sujet de la phrase — « Sur un Nuage, le 27
  // septembre passe de trente-quatre à soixante-dix-neuf euros » — et le
  // libellé du champ (« prix par nuit ») ne se dit plus : il ferait un sujet
  // de plus dans une phrase qui en a déjà un. Partout ailleurs, `quand` est
  // absent et la composition ne bouge pas d'un caractère.
  const quand = presentable(e.quand) ? direValeur(e.quand) : null;
  const composer = (libelle, actuel) => {
    const corps = [];
    // La virgule, pas les deux-points, quand une date suit : « Sur un Nuage,
    // le 27 septembre … » se dit, « Sur un Nuage : le 27 septembre … » non.
    if (nom) corps.push(quand ? `${nom},` : `${nom} :`);
    else if (innommable) corps.push(quand ? `${SANS_NOM},` : `${SANS_NOM} :`);
    if (quand) corps.push(quand);
    else if (presentable(libelle)) corps.push(direValeur(libelle));
    // ⚠️ `unSeulFait` NE S'APPLIQUE QUE SOUS `quand` (revue adverse M6). La
    // première version le passait sur TOUS les aperçus : mesuré sur les
    // valeurs réelles du relevé, 2 sur 7 y perdaient leur sens — « épinglé
    // (secteur boulangeries, ajouté à la main) » perdait sa parenthèse, et
    // « parmi 7 lieu(x) » devenait « parmi 7 lieu ». La claim « aucun des
    // énoncés déjà éprouvés ne bouge » était fausse. Réduire à un fait n'a de
    // sens que là où la DATE porte déjà le sujet et où la lecture empile ses
    // repères après le chiffre — c'est-à-dire sous `quand`.
    const brut = presentable(actuel) ? actuel : null;
    const dit =
      brut === null ? null : direValeur(quand ? unSeulFait(brut) : brut);
    const suite = aUneSuite
      ? `${direValeur(e.nouvelleLisible)}${
          presentable(e.unite) ? ` ${String(e.unite).trim()}` : ""
        }`
      : null;
    if (dit && suite && quand && !_estChiffree(dit)) {
      // ══ ⚖ IL N'Y AVAIT PAS DE VALEUR AVANT — CHEMIN ARGENT ═════════════
      //
      // ⚠️ MESURÉ PAR LA REVUE ADVERSE (M7) SUR UN CAS RÉEL DU RELEVÉ : la
      // lecture rend « aucun prix sur 1 nuit · plancher 34 € », et « passe de
      // aucun prix sur 1 nuit à 79 € » n'est pas du français. C'est l'énoncé
      // qui engage le clic : on ne le laisse pas boiter.
      corps.push(`: ${dit} — ce sera ${suite}`);
    } else if (dit && suite && quand) {
      // ⚠️ « PASSE DE … À … », ET SEULEMENT SOUS `quand`. Avec une date pour
      // sujet, « le 27 septembre trente-quatre euros devient … » n'est pas du
      // français. Sans date, le sujet est un champ (« le taux de commission
      // 22 % devient 20 ») et la forme d'avant se lit très bien : on ne la
      // touche pas, et aucun des énoncés déjà éprouvés ne bouge.
      // ⚠️ L'UNITÉ RESTE DES DEUX CÔTÉS, ET C'EST MESURÉ. La retirer du
      // premier terme (« passe de 34 à 79 € ») sortait « 34 » de l'entonnoir
      // de `voix.js` : `montantDit` ne voit plus d'euro, ne convertit plus, et
      // ce qui sort du haut-parleur dépend alors du moteur de synthèse. Sur un
      // chiffre d'argent lu avant un clic, on ne sous-traite pas.
      corps.push(`passe de ${dit} à ${suite}`);
    } else {
      if (dit) corps.push(dit);
      if (suite) {
        corps.push("devient");
        corps.push(suite);
      }
    }
    let f = corps.join(" ").trim();
    if (f && !/[.!?…]$/.test(f)) f += ".";
    return f;
  };
  // ⚠️ `corps.length` d'avant, gardé pour `objetDicible` plus bas.
  const corps = composer(e.libelle, e.actuelLisible) ? [1] : [];
  let fait = composer(e.libelle, e.actuelLisible);
  // ══ LA QUEUE NE SAUTE JAMAIS — C'EST LA VALEUR QUI COMPTE (lot 3, résidu 1)
  //
  // ⚠️ `borner` GARDE LA TÊTE ET JETTE LA QUEUE. Or « devient irrécupérable »
  // est à la fin, et c'est LE mot que Dimitri doit avoir entendu avant de
  // cliquer un jugement qui ne se rejoue pas. Un libellé de dégradation peut
  // faire 160 caractères : borné en tête, l'énoncé disait tout sauf le
  // verdict. Quand ça déborde et qu'il y a une suite, c'est le LIBELLÉ qui
  // raccourcit, puis saute, puis l'actuel — jamais la suite.
  if (aUneSuite && fait.length > MAX_FAIT) {
    // Ce qu'il reste pour le libellé, l'actuel gardé d'abord (« à vérifier »
    // devient « irrécupérable » se lit mieux que « devient irrécupérable »).
    const place = (actuel) => MAX_FAIT - composer(null, actuel).length - 1;
    const court = (actuel) => {
      const budget = place(actuel);
      return budget >= 12 ? borner(e.libelle, budget) : null;
    };
    for (const [l, a] of [
      [court(e.actuelLisible), e.actuelLisible],
      [court(null), null],
      [null, e.actuelLisible],
      [null, null],
    ]) {
      fait = composer(l, a);
      if (fait.length <= MAX_FAIT) break;
    }
  }

  // ⚠️ RÈGLE DE RÔLE, PAS COMPARAISON FLOUE (Dimitri, 15/09 : « jamais deux
  // fois le même texte »). Si la prose vient de parler, l'énoncé 1 se réduit
  // au fait chiffré ; le titre ne revient que si elle s'est tue. On ne compare
  // PAS deux textes de modèle pour décider : une similarité se trompe, une
  // règle se lit.
  const proseVide = !String(e.prose || "").trim();
  if (proseVide && titre) fait = fait ? `${titre}. ${fait}` : `${titre}.`;
  // ⚠️ MAIS LA RÈGLE DE RÔLE NE DOIT PAS PRODUIRE LE SILENCE. Certains leviers
  // ne déclarent AUCUN aperçu : il n'y a alors pas de fait chiffré du tout, et
  // omettre le titre parce que la prose a parlé ne laisserait RIEN à dire.
  // Quand il n'y a rien d'autre, le titre EST le fait — MAIS il ne suffit pas
  // à décider (voir `objetDicible` juste dessous).
  const objetDicible =
    corps.length > 0 &&
    (presentable(e.libelle) ||
      presentable(e.actuelLisible) ||
      aUneSuite ||
      !!nom ||
      innommable);
  if (!fait && titre) fait = `${titre}.`;
  if (!fait) return null;
  // Si le titre fait déborder, c'est LUI qui saute — le fait chiffré, jamais.
  if (fait.length > MAX_FAIT && titre && proseVide) {
    const sansTitre = fait.slice(titre.length + 2);
    if (sansTitre.trim()) fait = sansTitre.trim();
  }
  fait = borner(fait, MAX_FAIT);

  // ── ÉNONCÉ 2 — LE RESTE ──────────────────────────────────────────────────
  //
  // ⚠️ LA GARDE SE LIT AU REGISTRE (`refuseALaVoix`), jamais sur le geste
  // servi : une carte d'ÉVÉNEMENT n'a pas de `marquages`, et les marquages
  // d'un geste sont d'affichage, pas d'autorisation.
  const verdict = refuseALaVoix(
    e.levier !== undefined ? e.levier : { levier: null },
  );
  let reste;
  let pose = true;
  if (e.bloque === true) {
    // ⚠️ LE BLOCAGE PASSE DEVANT, ET CE N'EST PAS UN RELÂCHEMENT : une carte
    // grise n'est lançable par PERSONNE, ni au clic ni à la voix. Autant dire
    // ce qui manque — c'est la seule phrase qui lui permette d'avancer. La
    // garde, elle, se rend au verdict (`refuseALaVoix`), pas dans ce texte.
    reste =
      String(e.raisonBlocage || "").trim() ||
      "Ce geste ne peut pas partir en l'état — regardez la carte.";
  } else if (verdict.refuse) {
    // ⚠️ LE REFUS D'ABORD, LE MOTIF ENSUITE — trouvé par le balayage des 116
    // leviers. Un levier portant les TROIS marquages composait 105 caractères
    // d'énumération, et le plafond de 140 emportait précisément la phrase qui
    // compte. Ce qui est vital se dit en premier ; c'est ce qui décore qui se
    // fait couper.
    //
    // ⚠️ ET AUCUN TOKEN D'ACQUIESCEMENT N'EST PRONONCÉ : « je ne le LANCE pas »
    // émettait `lance`, qui est une forme fermée à lui seul.
    // ⚠️ JAMAIS D'INVITATION SOUS UN GESTE QUI NE PEUT PAS PARTIR À LA VOIX.
    reste = `Je ne fais pas ça à la voix : cliquez-la. Ce geste ${verdict.motif}.`;
  } else if (!objetDicible) {
    // ⚠️ UN TITRE NE NOMME PAS SON OBJET (revue #1482, MAJEUR 6 métier).
    // « Réécrire les consignes du copilote » — de quel logement ? avec quel
    // texte ? L'ancien n'est gardé nulle part. À l'oreille, il n'a pas de quoi
    // décider : la carte se dit, mais elle ne se lance pas à la voix.
    reste =
      "Je ne peux pas vous la dire en entier — elle est à l'écran, cliquez-la.";
    pose = false;
  } else {
    // ⚠️ AUCUNE FORME DE `LANCE` NI D'`ANNULE`, PAS MÊME EN UN MOT.
    // « Dites-moi si je lance » émettait `lance` — une forme FERMÉE. Et
    // l'oreille rouvre juste après : un fragment d'écho suffisait à ce que
    // l'opérateur se lance lui-même.
    reste =
      e.vanneOuverte === true
        ? "Je vous écoute."
        : "Elle est prête : cliquez-la.";
  }

  // ⚠️ `faitNu` — LE FAIT SANS AUCUN PRÉFIXE. Le refus « je n'avais pas fini »
  // se recompose à chaque « oui » prématuré : s'il préfixait le fait DÉJÀ
  // préfixé, on entendrait « Je n'avais pas fini. Je n'avais pas fini. … »
  // jusqu'à la coupe des sept cents caractères.
  return { fait, faitNu: fait, reste: borner(reste, MAX_RESTE), pose };
}

/**
 * Ce qu'il dit quand DEUX cartes sont prêtes.
 *
 * ⚠️ PAR LE NOM, PAS PAR LE RANG (règle Dimitri, 16/09). Deux cartes de prix
 * se ressembleraient par leur seul titre : chacune se nomme par son titre ET
 * son logement. Et il REFUSE — deviner lancerait celle qu'il ne regardait pas.
 * La forme « oui pour <nom> » est le lot immédiatement suivant.
 */
export function phraseDeuxCartes(cartes) {
  const l = (Array.isArray(cartes) ? cartes : [])
    .map((c) => {
      // ⚠️ LE TITRE VIENT DU REGISTRE quand la carte n'en porte pas : une carte
      // d'ÉVÉNEMENT se compose en `{levier, args}`, sans titre. Sans ça, il
      // disait « Deux gestes sont prêts :  sur Cocon Naturel, ou … ».
      const duRegistre = c && c.levier ? lireLevier(c.levier) : null;
      const t = String(
        (c && c.titre) || (duRegistre && duRegistre.titre) || "",
      ).trim();
      const n = presentable(c && c.nomLogement)
        ? String(c.nomLogement).trim()
        : null;
      return n ? `${t} sur ${n}` : t;
    })
    .filter(Boolean);
  if (!l.length) return null;
  return borner(
    `Deux gestes sont prêts : ${l.join(", ou ")}. Je ne devine pas — cliquez celui que vous voulez.`,
    260,
  );
}

/** Ce qu'il dit quand l'aperçu n'est toujours pas là. Rien n'est posé comme lu. */
export const PAS_ENCORE_LU =
  "Je n'ai pas fini de lire la valeur actuelle — regardez la carte.";

/** Ce qu'il dit quand un « oui » arrive avant la fin du fait. */
export function refusTropTot(fait) {
  return `Je n'avais pas fini. ${coupeLisible(fait).texte}`;
}
